/**
 * PlaytestContext — einziger Zustand des Playtest-Bretts.
 *
 * Der Server ist die Autorität (`playtest:state:synced` liefert den vollen Stand, Einzel-Ereignisse
 * die Änderungen). Eigene Aktionen wirken sofort als Vormerkung: `OPTIMISTIC_APPLY` wendet eine
 * gewöhnliche Reducer-Aktion an und merkt sich sie samt Umkehr-Aktion unter `pending[requestId]`;
 * die Bestätigung des Servers (`OPTIMISTIC_CONFIRM` mit den Aktionen seines Ereignisses) löst die
 * Vormerkung auf, ein Fehler (`OPTIMISTIC_REVERT`) wendet die Umkehr an. Die Umkehr ist eine
 * Aktion und kein Schnappschuss, damit eine abgelehnte Änderung eine zweite, noch offene Änderung
 * derselben Karte nicht mitnimmt.
 *
 * Weil der Auslöser das Server-Ereignis seiner eigenen Aktion ebenfalls erhält, sind alle
 * Aktionen, die optimistisch laufen, wiederholbar: ein zweites Anwenden mit Serverdaten gleicht
 * nur ab, es verdoppelt nichts.
 */

import React, { createContext, useContext, useReducer, useMemo } from 'react';

const PlaytestContext = createContext();

const MAX_LOG_ENTRIES = 200;
const MAX_PINGS = 20;
/** Spiegel von PING_VISIBLE_MS in backend/services/playtestRules.ts: so lange steht der Ring eines Hinweises. */
export const PING_VISIBLE_MS = 2000;
const CARD_ZONES = ['hand', 'battlefield', 'graveyard', 'exile', 'commandZone', 'deck'];

export const initialState = {
    sessionId: null,
    hostId: null,
    status: null,
    currentUserId: null,
    players: [],
    currentPlayerPosition: null,

    isInMulliganPhase: false,
    isCommanderMode: false,
    // Empfehlung nach einem Mulligan, wie viele Karten unter die Bibliothek gehören — nur ein
    // Hinweis, „Behalten" erzwingt die Anzahl nicht.
    cardsToBottomCount: 0,
    currentTurnUserId: null,
    turnNumber: 0,

    // Offene Vormerkungen: requestId → { action, inverse, createdAt }
    pending: {},

    sessionLog: [],
    lastDiceRoll: null,
    // Hinweise auf Brettkarten: { userId, instanceId, at }, älteste zuerst
    pings: [],
    handViewRequest: null,
    revealedHand: null,
    controlRequest: null,
    // Das Brett ist für diesen Nutzer geschlossen: { message, code } — Host hat beendet
    // ('SESSION_ENDED'), Session unbekannt, kein Teilnehmer, Anmeldung abgelaufen.
    boardClosed: null,
};

// ============================================================
// HILFSFUNKTIONEN
// ============================================================

function sameUser(a, b) {
    return a === b || String(a) === String(b);
}

function findPlayer(players, userId) {
    if (!Array.isArray(players)) return undefined;
    return players.find(p => sameUser(p.userId, userId));
}

function findPlayerByPosition(players, position) {
    if (!Array.isArray(players)) return undefined;
    return players.find(p => p.position === position);
}

function updatePlayer(players, userId, updateFn) {
    if (!Array.isArray(players)) return [];
    return players.map(player => (sameUser(player.userId, userId) ? updateFn(player) : player));
}

function hasCard(cards, instanceId) {
    return Array.isArray(cards) && cards.some(c => c && c.instanceId === instanceId);
}

/** Wendet `fn` auf die Karte `instanceId` an, in jeder Zone, in der sie liegt. */
function updateCardInZones(player, instanceId, fn, zones = CARD_ZONES) {
    const next = { ...player };
    for (const zone of zones) {
        if (!Array.isArray(player[zone])) continue;
        next[zone] = player[zone].map(c => (c && c.instanceId === instanceId ? fn(c) : c));
    }
    return next;
}

function updateCardOf(state, playerId, instanceId, fn, zones) {
    return {
        ...state,
        players: updatePlayer(state.players, playerId, player => updateCardInZones(player, instanceId, fn, zones)),
    };
}

/**
 * Fügt eine Karte in eine Zone ein. `index` setzt sie an eine feste Stelle (Umkehr einer
 * Bewegung); in die Bibliothek kommt sie oben (Array-Ende) nur mit `insertAtTop === true`,
 * sonst unten — dieselbe Regel wie im Server.
 */
function insertCard(cards, card, { zone, insertAtTop, index }) {
    if (typeof index === 'number') {
        const at = Math.max(0, Math.min(index, cards.length));
        return [...cards.slice(0, at), card, ...cards.slice(at)];
    }
    if (zone === 'deck' && insertAtTop !== true) return [card, ...cards];
    return [...cards, card];
}

/** Nimmt eine Karte aus der Zone; fehlt sie, steht sie dort verdeckt als `null`-Platzhalter. */
function removeCard(cards, instanceId) {
    if (hasCard(cards, instanceId)) return cards.filter(c => !(c && c.instanceId === instanceId));
    const placeholder = cards.lastIndexOf(null);
    if (placeholder === -1) return cards;
    return [...cards.slice(0, placeholder), ...cards.slice(placeholder + 1)];
}

function vanishesOffBattlefield(card, fromZone, toZone) {
    return fromZone === 'battlefield' && toZone !== 'battlefield' && !!card && (card.isToken === true || card.isCopy === true);
}

// ============================================================
// REDUCER
// ============================================================

/**
 * Ist die Bewegung schon angewendet (Server-Echo einer eigenen Vormerkung)? Dann liegt die Karte
 * bereits im Ziel, oder sie fehlt in einer vollständig bekannten Quellzone — Letzteres bei Karten,
 * die beim Verlassen des Bretts verschwinden oder zum Besitzer wandern.
 */
function isMoveAlreadyApplied(source, target, { instanceId, fromZone, toZone }) {
    if (fromZone === toZone) return false;
    const sourceZone = source?.[fromZone];
    if (hasCard(sourceZone, instanceId)) return false;
    if (hasCard(target?.[toZone], instanceId)) return true;
    return Array.isArray(sourceZone) && !sourceZone.includes(null);
}

/**
 * CARD_MOVED { playerId, targetPlayerId?, instanceId, fromZone, toZone, card?, insertAtTop?, index?, isUndo? }
 * `targetPlayerId` weicht von `playerId` ab, wenn eine fremd kontrollierte Karte das Brett
 * verlässt: sie geht in die Zone ihres Besitzers. Tokens und Kopien verschwinden dann ganz
 * (Server: `toZone = 'deleted'`). `isUndo` kennzeichnet die Umkehr einer Vormerkung: sie legt die
 * Karte immer zurück, auch wenn die Quellzone sie nicht (mehr) enthält.
 */
function reduceCardMoved(state, payload) {
    const { playerId, instanceId, fromZone, toZone, card, insertAtTop, index } = payload;
    const targetId = payload.targetPlayerId ?? playerId;
    const source = findPlayer(state.players, playerId);
    const target = findPlayer(state.players, targetId);

    if (!payload.isUndo && isMoveAlreadyApplied(source, target, payload)) {
        if (!card || !hasCard(target?.[toZone], instanceId)) return state;
        return updateCardOf(state, targetId, instanceId, () => card, [toZone]);
    }

    const vanishes = vanishesOffBattlefield(card, fromZone, toZone);
    let players = updatePlayer(state.players, playerId, player => {
        const next = { ...player };
        if (Array.isArray(player[fromZone])) next[fromZone] = removeCard(player[fromZone], instanceId);
        if (fromZone === 'hand') next.handCount = Math.max(0, (player.handCount || 0) - 1);
        return next;
    });
    if (!vanishes) {
        players = updatePlayer(players, targetId, player => {
            const next = { ...player };
            if (card && Array.isArray(player[toZone])) {
                next[toZone] = insertCard(player[toZone], card, { zone: toZone, insertAtTop, index });
            }
            if (toZone === 'hand') next.handCount = (next.handCount || 0) + 1;
            return next;
        });
    }
    return { ...state, players };
}

/** CARD_PLAYED { playerId, instanceId, card } — aus der Hand (oder Bibliothek) aufs Brett. */
function reduceCardPlayed(state, { playerId, instanceId, card }) {
    return {
        ...state,
        players: updatePlayer(state.players, playerId, player => {
            if (hasCard(player.battlefield, instanceId)) {
                return updateCardInZones(player, instanceId, () => card, ['battlefield']);
            }
            const fromHand = !Array.isArray(player.hand) || hasCard(player.hand, instanceId);
            return {
                ...player,
                hand: Array.isArray(player.hand) ? player.hand.filter(c => !(c && c.instanceId === instanceId)) : player.hand,
                deck: Array.isArray(player.deck) ? player.deck.filter(c => !(c && c.instanceId === instanceId)) : player.deck,
                handCount: fromHand ? Math.max(0, (player.handCount || 0) - 1) : player.handCount,
                battlefield: card ? [...(player.battlefield || []), card] : (player.battlefield || []),
            };
        }),
    };
}

/** PLAYER_COUNTER_ADJUSTED { userId, counterId, delta?, value?, name?, icon? } — wie der Server: nie unter 0, fehlende Marke entsteht. */
function reducePlayerCounterAdjusted(state, { userId, counterId, delta, value, name, icon }) {
    return {
        ...state,
        players: updatePlayer(state.players, userId, player => {
            const counters = player.counters || [];
            const existing = counters.find(c => c.id === counterId);
            const base = existing ?? { id: counterId, name: name ?? counterId, icon: icon ?? null, value: 0 };
            const updated = { ...base, value: Math.max(0, value ?? base.value + (delta ?? 0)) };
            return {
                ...player,
                counters: existing ? counters.map(c => (c.id === counterId ? updated : c)) : [...counters, updated],
            };
        }),
    };
}

export function playtestReducer(state, action) {
    const payload = action.payload;
    switch (action.type) {
        // ---------------- Session ----------------

        case 'INITIALIZE_SESSION': {
            const players = payload.players || [];
            const currentUserId = payload.currentUserId || state.currentUserId;
            return {
                ...state,
                sessionId: payload.sessionId,
                hostId: payload.hostId ?? state.hostId,
                status: payload.status || state.status,
                currentUserId,
                players,
                currentPlayerPosition: findPlayer(players, currentUserId)?.position ?? state.currentPlayerPosition,
                pending: {},
                boardClosed: null,
            };
        }

        case 'STATE_SYNCED': {
            const players = payload.players || [];
            const currentUserId = payload.currentUserId || state.currentUserId;
            return {
                ...state,
                sessionId: payload.sessionId ?? state.sessionId,
                hostId: payload.hostId ?? state.hostId,
                status: payload.status ?? state.status,
                currentUserId,
                players,
                currentPlayerPosition: findPlayer(players, currentUserId)?.position ?? state.currentPlayerPosition,
                isInMulliganPhase: payload.isInMulliganPhase ?? state.isInMulliganPhase,
                isCommanderMode: payload.isCommanderMode ?? state.isCommanderMode,
                currentTurnUserId: payload.currentTurnUserId !== undefined ? payload.currentTurnUserId : state.currentTurnUserId,
                turnNumber: payload.turnNumber ?? state.turnNumber,
                sessionLog: payload.sessionLog ?? state.sessionLog,
                // Der Serverstand enthält alles, was der Server verarbeitet hat; offene
                // Vormerkungen sind damit abgelöst.
                pending: {},
                boardClosed: null,
            };
        }

        case 'BOARD_CLOSED':
            return { ...state, boardClosed: { message: payload.message, code: payload.code ?? null } };

        case 'RESET':
            return initialState;

        // ---------------- Vormerkungen ----------------

        case 'OPTIMISTIC_APPLY': {
            const { requestId, inverse, createdAt } = payload;
            const applied = playtestReducer(state, payload.action);
            return {
                ...applied,
                pending: { ...applied.pending, [requestId]: { action: payload.action, inverse, createdAt: createdAt ?? Date.now() } },
            };
        }

        case 'OPTIMISTIC_CONFIRM': {
            // payload: { requestId, actions? } — `actions` sind die Reducer-Aktionen des bestätigenden
            // Server-Ereignisses. Sie tragen absolute Werte (Leben, Counter, Marken-Liste) und würden
            // die übrigen offenen Vormerkungen auf denselben Werten überschreiben; deshalb werden
            // diese vorher umgekehrt und danach in ihrer Reihenfolge erneut angewendet. Das gilt
            // auch für eine unbekannte requestId (Vormerkung schon durch einen Vollstand abgelöst).
            const actions = payload.actions || [];
            if (!state.pending[payload.requestId] && actions.length === 0) return state;
            const { [payload.requestId]: _confirmed, ...pending } = state.pending;
            const others = Object.values(pending).sort((a, b) => a.createdAt - b.createdAt);
            let next = [...others].reverse().reduce((acc, entry) => playtestReducer(acc, entry.inverse), state);
            next = actions.reduce(playtestReducer, next);
            next = others.reduce((acc, entry) => playtestReducer(acc, entry.action), next);
            return { ...next, pending };
        }

        case 'OPTIMISTIC_REVERT': {
            const entry = state.pending[payload.requestId];
            if (!entry) return state;
            const { [payload.requestId]: _reverted, ...pending } = state.pending;
            return { ...playtestReducer(state, entry.inverse), pending };
        }

        // ---------------- Karten ----------------

        case 'CARDS_DRAWN': {
            // payload: { playerId, cards, deckRemaining } — eigene Karten mit Details
            const cards = payload.cards || [];
            return {
                ...state,
                players: updatePlayer(state.players, payload.playerId, player => ({
                    ...player,
                    hand: [...(player.hand || []), ...cards],
                    handCount: (player.handCount || 0) + cards.length,
                    deck: (player.deck ?? []).slice(0, payload.deckRemaining),
                })),
            };
        }

        case 'OPPONENT_DRAWN': {
            // payload: { playerId, count } — fremdes Ziehen, nur die Anzahl; gezogen wird von oben (Array-Ende)
            const count = payload.count || 1;
            return {
                ...state,
                players: updatePlayer(state.players, payload.playerId, player => {
                    const deck = player.deck || [];
                    return {
                        ...player,
                        handCount: (player.handCount || 0) + count,
                        deck: deck.slice(0, Math.max(0, deck.length - count)),
                    };
                }),
            };
        }

        case 'CARD_PLAYED':
            return reduceCardPlayed(state, payload);

        case 'CARD_MOVED':
            return reduceCardMoved(state, payload);

        case 'CARD_TAPPED':
            return updateCardOf(state, payload.playerId, payload.instanceId, c => ({ ...c, tapped: payload.tapped }), ['battlefield']);

        case 'ALL_UNTAPPED':
            return {
                ...state,
                players: updatePlayer(state.players, payload.playerId, player => ({
                    ...player,
                    battlefield: (player.battlefield || []).map(c =>
                        c && payload.instanceIds.includes(c.instanceId) ? { ...c, tapped: false } : c
                    ),
                })),
            };

        case 'CARD_POSITION_UPDATED':
            // payload: { playerId, instanceId, position, zIndex? }
            return updateCardOf(state, payload.playerId, payload.instanceId, c => ({
                ...c,
                position: payload.position,
                ...(payload.zIndex !== undefined && { zIndex: payload.zIndex }),
            }), ['battlefield']);

        case 'CARD_Z_INDEX_CHANGED':
            return updateCardOf(state, payload.playerId, payload.instanceId, c => ({ ...c, zIndex: payload.zIndex }), ['battlefield']);

        case 'CARD_TRANSFORMED':
            // Marken kommen absolut vom Server (Startmarken der Seite); die Vormerkung dreht nur die Seite.
            return updateCardOf(state, payload.playerId, payload.instanceId, c => ({
                ...c,
                transformedFace: payload.transformedFace,
                ...(payload.counters && { counters: payload.counters }),
            }));

        case 'CARD_FACEDOWN_CHANGED':
            // payload: { playerId, instanceId, faceDown, card? } — beim Aufdecken liefert der Server
            // die Karte nach, die bis dahin für fremde Augen maskiert war.
            return updateCardOf(state, payload.playerId, payload.instanceId, c => (
                payload.card ? { ...payload.card, faceDown: payload.faceDown } : { ...c, faceDown: payload.faceDown }
            ));

        case 'COUNTERS_UPDATED':
            // payload: { playerId, instanceId, counterType, newValue } vom Server oder { …, delta } als
            // Vormerkung — die Differenz, damit zwei offene Änderungen sich nicht überschreiben.
            return updateCardOf(state, payload.playerId, payload.instanceId, c => {
                const current = c.counters?.[payload.counterType] || 0;
                const value = payload.newValue ?? current + payload.delta;
                const { [payload.counterType]: _old, ...rest } = c.counters || {};
                return { ...c, counters: value > 0 ? { ...rest, [payload.counterType]: value } : rest };
            }, ['battlefield', 'graveyard', 'exile']);

        case 'MARKERS_UPDATED':
            // payload: { playerId, instanceId, markerType, enabled, markers }
            return updateCardOf(state, payload.playerId, payload.instanceId, c => (
                payload.markerType === 'all' && !payload.enabled
                    ? { ...c, markers: payload.markers || {} }
                    : { ...c, markers: { ...c.markers, [payload.markerType]: payload.enabled } }
            ), ['battlefield', 'graveyard', 'exile']);

        case 'TOKEN_CREATED':
            return {
                ...state,
                players: updatePlayer(state.players, payload.playerId, player => ({
                    ...player,
                    battlefield: [...(player.battlefield || []), payload.token],
                })),
            };

        case 'TOKEN_ADDED_TO_AVAILABLE': {
            const { token } = payload;
            return {
                ...state,
                players: updatePlayer(state.players, payload.playerId, player => {
                    if ((player.tokensAvailable || []).some(t => t.cardId === token.cardId)) return player;
                    return {
                        ...player,
                        tokensAvailable: [...(player.tokensAvailable || []), {
                            cardId: token.cardId,
                            scryfallData: {
                                id: token.cardId,
                                name: token.name,
                                imageUrl: token.imageUrl,
                                imageUrlBack: token.imageUrlBack,
                                typeLine: token.typeLine,
                                power: token.power,
                                toughness: token.toughness,
                                colorIdentity: token.colorIdentity,
                            },
                        }],
                    };
                }),
            };
        }

        case 'DECK_TOP_REVEALED':
            // payload: { playerId, topCard } — neue Oberkarte einer offen gespielten fremden
            // Bibliothek (null: Bibliothek leer). Die eigene Bibliothek ist ohnehin vollständig bekannt.
            return {
                ...state,
                players: updatePlayer(state.players, payload.playerId, player => {
                    const deck = player.deck || [];
                    if (sameUser(player.userId, state.currentUserId) || deck.length === 0) return player;
                    return { ...player, deck: [...deck.slice(0, -1), payload.topCard ?? null] };
                }),
            };

        case 'DECK_OPEN_PLAY_CHANGED':
            // payload: { playerId, deckOpenPlay, topCard? } — die oberste Karte (Array-Ende) wird für
            // die anderen sichtbar bzw. wieder verborgen.
            return {
                ...state,
                players: updatePlayer(state.players, payload.playerId, player => {
                    const deck = player.deck ? [...player.deck] : [];
                    const isOwn = sameUser(player.userId, state.currentUserId);
                    if (!isOwn && deck.length > 0) {
                        deck[deck.length - 1] = payload.deckOpenPlay && payload.topCard ? payload.topCard : null;
                    }
                    return { ...player, deckOpenPlay: payload.deckOpenPlay, deck };
                }),
            };

        // ---------------- Teilnehmer ----------------

        case 'LIFE_UPDATED':
            // payload: { playerId, newLife, eliminated? } vom Server oder { playerId, delta } als Vormerkung
            return {
                ...state,
                players: updatePlayer(state.players, payload.playerId, player => ({
                    ...player,
                    life: payload.newLife ?? player.life + payload.delta,
                    ...(payload.eliminated === true && { playerStatus: 'eliminated' }),
                })),
            };

        case 'COMMANDER_DAMAGE_UPDATED':
            // payload: { targetUserId, newCommanderDamage, newLife } vom Server oder
            // { targetUserId, sourceUserId, delta } als Vormerkung (Commander-Schaden zieht Leben ab)
            return {
                ...state,
                players: updatePlayer(state.players, payload.targetUserId, player => {
                    if (payload.newCommanderDamage) {
                        return { ...player, life: payload.newLife, commanderDamage: payload.newCommanderDamage };
                    }
                    const damage = player.commanderDamage || {};
                    return {
                        ...player,
                        life: player.life - payload.delta,
                        commanderDamage: { ...damage, [payload.sourceUserId]: (damage[payload.sourceUserId] || 0) + payload.delta },
                    };
                }),
            };

        case 'PLAYER_COUNTERS_CHANGED':
            return {
                ...state,
                players: updatePlayer(state.players, payload.userId, player => ({ ...player, counters: payload.counters })),
            };

        case 'PLAYER_COUNTER_ADJUSTED':
            return reducePlayerCounterAdjusted(state, payload);

        case 'PLAYER_COUNTER_REMOVED':
            return {
                ...state,
                players: updatePlayer(state.players, payload.userId, player => ({
                    ...player,
                    counters: (player.counters || []).filter(c => c.id !== payload.counterId),
                })),
            };

        case 'PLAYER_ELIMINATED':
            return {
                ...state,
                players: updatePlayer(state.players, payload.userId, player => ({ ...player, playerStatus: 'eliminated' })),
            };

        case 'PLAYER_PRESENCE_CHANGED':
            return {
                ...state,
                players: updatePlayer(state.players, payload.userId, player => ({ ...player, isAway: payload.isAway })),
            };

        case 'DICE_ROLLED':
            // payload: { userId, displayName, sides, result } — `rollId` unterscheidet zwei gleiche Würfe
            return { ...state, lastDiceRoll: { ...payload, rollId: (state.lastDiceRoll?.rollId ?? 0) + 1 } };

        case 'PING_RECEIVED': {
            const pings = [...state.pings, { userId: payload.userId, instanceId: payload.instanceId, at: payload.at }];
            return { ...state, pings: pings.slice(-MAX_PINGS) };
        }

        case 'PING_EXPIRED':
            return { ...state, pings: state.pings.filter(p => !(p.instanceId === payload.instanceId && p.at === payload.at)) };

        // ---------------- Hand-Einsicht und Kontrolle ----------------

        case 'HAND_VIEW_REQUESTED':
            return { ...state, handViewRequest: { requesterId: payload.requesterId, requesterName: payload.requesterName } };

        case 'HAND_VIEW_REQUEST_CLEARED':
            return { ...state, handViewRequest: null };

        case 'HAND_REVEALED':
            return { ...state, revealedHand: { targetUserId: payload.targetUserId, targetName: payload.targetName, cards: payload.cards } };

        case 'HAND_REVEALED_CLEARED':
            return { ...state, revealedHand: null };

        case 'CONTROL_REQUESTED':
            return {
                ...state,
                controlRequest: {
                    requesterId: payload.requesterId,
                    requesterName: payload.requesterName,
                    instanceId: payload.instanceId,
                    cardName: payload.cardName,
                },
            };

        case 'CONTROL_REQUEST_CLEARED':
            return { ...state, controlRequest: null };

        case 'CARD_CONTROL_CHANGED':
        case 'CARD_CONTROL_RETURNED': {
            // payload: { ownerId, controllerId, instanceId, card } — die Karte wechselt das Brett:
            // beim Übernehmen vom Besitzer zum Kontrollierenden, beim Zurückgeben umgekehrt. Eine
            // offene Anfrage an mich endet nur, wenn sie genau diese Karte betraf.
            const toOwner = action.type === 'CARD_CONTROL_RETURNED';
            const fromId = toOwner ? payload.controllerId : payload.ownerId;
            const toId = toOwner ? payload.ownerId : payload.controllerId;
            const players = (state.players || []).map(p => {
                if (sameUser(p.userId, fromId)) {
                    return { ...p, battlefield: (p.battlefield || []).filter(c => c.instanceId !== payload.instanceId) };
                }
                if (sameUser(p.userId, toId) && !hasCard(p.battlefield, payload.instanceId)) {
                    return { ...p, battlefield: [...(p.battlefield || []), payload.card] };
                }
                return p;
            });
            const settlesRequest = !toOwner && state.controlRequest?.instanceId === payload.instanceId;
            return { ...state, players, controlRequest: settlesRequest ? null : state.controlRequest };
        }

        // ---------------- Protokoll ----------------

        case 'LOG_ENTRY_ADDED': {
            const log = state.sessionLog.length >= MAX_LOG_ENTRIES ? state.sessionLog.slice(1) : state.sessionLog;
            return { ...state, sessionLog: [...log, payload] };
        }

        // ---------------- Mulligan und Zug ----------------

        case 'MULLIGAN_TAKEN':
            // payload: { userId, mulliganCount, cardsToBottomCount, newHand?, deckRemaining? } —
            // newHand nur für den eigenen Teilnehmer
            return {
                ...state,
                cardsToBottomCount: sameUser(payload.userId, state.currentUserId)
                    ? payload.cardsToBottomCount
                    : state.cardsToBottomCount,
                players: updatePlayer(state.players, payload.userId, player => {
                    const next = { ...player, mulliganCount: payload.mulliganCount };
                    if (payload.newHand !== undefined) {
                        next.hand = payload.newHand;
                        next.handCount = payload.newHand.length;
                        if (payload.deckRemaining !== undefined) next.deck = new Array(payload.deckRemaining).fill(null);
                    }
                    return next;
                }),
            };

        case 'MULLIGAN_HAND_KEPT':
            // payload: { hand, deckRemaining } — eigene Hand, nachdem gewählte Karten unter die Bibliothek gingen
            return {
                ...state,
                players: updatePlayer(state.players, state.currentUserId, player => ({
                    ...player,
                    hand: payload.hand,
                    handCount: payload.hand.length,
                    deck: new Array(payload.deckRemaining).fill(null),
                })),
            };

        case 'MULLIGAN_PLAYER_KEPT':
            return {
                ...state,
                players: updatePlayer(state.players, payload.userId, player => ({
                    ...player,
                    mulliganKept: true,
                    mulliganCount: payload.mulliganCount,
                })),
            };

        case 'MULLIGAN_PHASE_ENDED':
            return {
                ...state,
                isInMulliganPhase: false,
                cardsToBottomCount: 0,
                currentTurnUserId: payload.currentTurnUserId,
                turnNumber: payload.turnNumber,
            };

        case 'TURN_CHANGED':
            return { ...state, currentTurnUserId: payload.newUserId, turnNumber: payload.turnNumber };

        default:
            return state;
    }
}

// ============================================================
// PROVIDER UND ZUGRIFF
// ============================================================

export function PlaytestProvider({ children }) {
    const [state, dispatch] = useReducer(playtestReducer, initialState);
    const value = useMemo(() => ({ state, dispatch }), [state]);

    return (
        <PlaytestContext.Provider value={value}>
            {children}
        </PlaytestContext.Provider>
    );
}

export function usePlaytest() {
    const context = useContext(PlaytestContext);
    if (!context) {
        throw new Error('usePlaytest muss innerhalb PlaytestProvider verwendet werden');
    }
    return context;
}

export const playtestSelectors = {
    getCurrentPlayer: (state) => findPlayer(state.players, state.currentUserId),
    getOpponents: (state) => (state.players || []).filter(p => !sameUser(p.userId, state.currentUserId)),
    getPlayerByPosition: (state, position) => findPlayerByPosition(state.players, position),
    // Fremde Hände kennt der Client nur als Anzahl.
    getOpponentHandCount: (state, opponentUserId) => {
        const opponent = findPlayer(state.players, opponentUserId);
        return opponent?.handCount ?? opponent?.hand?.length ?? 0;
    },
};

export const playtestActions = {
    initializeSession: (sessionData) => ({ type: 'INITIALIZE_SESSION', payload: sessionData }),
};
